import * as XLSX from 'xlsx';
import { buildTopLists, consolidateHoldings } from './portfolio';

// Indian Financial Year (Apr-Mar) label for a date, e.g. "FY 2024-25".
const getFY = (dateStr) => {
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const startYear = d.getMonth() >= 3 ? y : y - 1;
  return `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
};

const EQUITY_TAX_TYPES = ['Equity', 'ETF', 'Equity Mutual Fund'];
const isEquityTax = (t) => EQUITY_TAX_TYPES.includes(t || 'Equity');

// Export a (already sorted) consolidated holdings list to Excel.
export const exportHoldingsListToExcel = (holdings, entityInfo, sortLabel, priceStats = {}) => {
  const wb = XLSX.utils.book_new();
  const pctFrom = (cur, high) => (high && high > 0 ? Number((((cur - high) / high) * 100).toFixed(2)) : null);
  const rows = holdings.map((h, i) => {
    const st = priceStats[h.ticker] || {};
    return {
      Rank: i + 1,
      Ticker: h.ticker,
      'Company Name': h.shareName,
      Type: h.instrumentType,
      Exchange: h.exchange,
      Quantity: Number((h.quantity || 0).toFixed(3)),
      'Avg Cost (Rs)': Number((h.avgPrice || 0).toFixed(2)),
      'Prev. Close (Rs)': h.previousClose ?? '',
      'LTP (Rs)': Number((h.currentMarketPrice || 0).toFixed(2)),
      'Latest Value (Rs)': Number((h.currentValue || 0).toFixed(2)),
      "Today's Gain (Rs)": Number((h.dayChange || 0).toFixed(2)),
      "Today's Gain %": Number((h.dayChangePercent || 0).toFixed(2)),
      'Overall Gain (Rs)': Number((h.profit || 0).toFixed(2)),
      'Overall Gain %': Number((h.returnPercentage || 0).toFixed(2)),
      '52W High (Rs)': st.week52High ?? '',
      '52W Low (Rs)': st.week52Low ?? '',
      '% from 52W High': pctFrom(h.currentMarketPrice, st.week52High) ?? '',
      'All-Time High (Rs)': st.allTimeHigh ?? '',
      'All-Time Low (Rs)': st.allTimeLow ?? '',
      '% from All-Time High': pctFrom(h.currentMarketPrice, st.allTimeHigh) ?? '',
    };
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [{ wch: 6 }, { wch: 14 }, { wch: 32 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, ws, `Holdings (by ${sortLabel || 'Value'})`.slice(0, 31));
  const fileDate = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `Holdings_${(entityInfo.entityName || 'Portfolio').replace(/[^a-z0-9]/gi, '_')}_${fileDate}.xlsx`);
};

export const exportDailySummaryToExcel = (transactions, entityInfo, summary) => {
  const wb = XLSX.utils.book_new();
  const lists = buildTopLists(transactions);
  const s = summary || {};
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  // Sheet 1: Summary metrics
  const summaryRows = [
    { Metric: 'Entity', Value: entityInfo.entityName },
    { Metric: 'Portfolio ID', Value: entityInfo.portfolioId },
    { Metric: 'PAN', Value: entityInfo.panNumber },
    { Metric: 'Report Date', Value: today },
    { Metric: 'Total Invested (Rs)', Value: s.totalInvestment ?? '' },
    { Metric: 'Current Value (Rs)', Value: s.totalCurrentValue ?? '' },
    { Metric: 'Overall P/L (Rs)', Value: s.totalProfit ?? '' },
    { Metric: 'Overall Return (%)', Value: s.totalReturnPercent ?? '' },
    { Metric: "Today's Gain/Loss (Rs)", Value: s.todaysGainLoss ?? '' },
    { Metric: "Today's Gain/Loss (%)", Value: s.todaysGainLossPercent ?? '' },
    { Metric: 'Portfolio CAGR / XIRR (%)', Value: s.xirr ?? 'N/A' },
    { Metric: 'Total Stocks (consolidated)', Value: lists.holdings.length },
  ];
  const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
  wsSummary['!cols'] = [{ wch: 30 }, { wch: 35 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  const mapStock = (h, rankByPct) => ({
    Rank: 0,
    Ticker: h.ticker,
    'Share Name': h.shareName,
    Type: h.instrumentType,
    Exchange: h.exchange,
    Quantity: h.quantity,
    'Avg Price': Number(h.avgPrice.toFixed(2)),
    'Current Price': h.currentMarketPrice,
    'Current Value (Rs)': Number(h.currentValue.toFixed(2)),
    'P&L (Rs)': Number(h.profit.toFixed(2)),
    'Return %': Number(h.returnPercentage.toFixed(2)),
    "Today's G/L (Rs)": Number(h.dayChange.toFixed(2)),
  });

  const addSheet = (name, rows) => {
    const data = rows.map((h, i) => ({ ...mapStock(h), Rank: i + 1 }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 6 }, { wch: 14 }, { wch: 32 }, { wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws, name);
  };

  addSheet('Top 25 by Value', lists.topByValue);
  addSheet('Top 25 Gainers (%)', lists.topGainersPct);
  addSheet('Top 25 Losers (%)', lists.topLosersPct);
  addSheet('Top 25 Gainers (Rs)', lists.topGainersValue);
  addSheet('Top 25 Losers (Rs)', lists.topLosersValue);

  const fileDate = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `Daily_Summary_${(entityInfo.entityName || 'Portfolio').replace(/[^a-z0-9]/gi, '_')}_${fileDate}.xlsx`);
};

export const exportTransactionsToExcel = (transactions, entityInfo, portfolios = []) => {
  const pmap = Object.fromEntries((portfolios || []).map((p) => [p.id, p.name]));
  // Prepare transaction data
  const transactionData = transactions.map(txn => ({
    'Portfolio': pmap[txn.portfolioId] || '—',
    'Financial Year': getFY(txn.purchaseDate),
    'Ticker': txn.ticker,
    'Share Name': txn.shareName,
    'Instrument Type': txn.instrumentType || 'Equity',
    'Exchange': txn.exchange,
    'Quantity': txn.quantity,
    'Purchase Price (₹)': txn.purchasePrice,
    'Purchase Date': txn.purchaseDate,
    'Current Market Price (₹)': txn.currentMarketPrice,
    'Sell Price (₹)': txn.sellPrice || '-',
    'Sell Date': txn.sellDate || '-',
    'Total Cost (₹)': txn.totalCost,
    'Current Value (₹)': txn.currentValue,
    'Profit/Loss (₹)': txn.profit,
    'Return %': txn.returnPercentage,
    'CAGR %': txn.cagr,
    'Status': txn.status === 'sold' ? 'Sold' : 'Held',
    'Notes': txn.notes || '',
  }));

  // Create workbook
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(transactionData);
  ws['!cols'] = [{ wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 30 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 30 }];
  XLSX.utils.book_append_sheet(wb, ws, 'All Transactions');

  // Financial Year-wise transaction summary
  const fyGroups = {};
  transactions.forEach((t) => {
    const fy = getFY(t.purchaseDate);
    const g = fyGroups[fy] || { fy, count: 0, invested: 0, currentValue: 0, pl: 0 };
    g.count += 1;
    g.invested += t.totalCost || 0;
    g.currentValue += t.currentValue || 0;
    g.pl += t.profit || 0;
    fyGroups[fy] = g;
  });
  const fyRows = Object.values(fyGroups).sort((a, b) => b.fy.localeCompare(a.fy)).map((g) => ({
    'Financial Year': g.fy,
    'Transactions': g.count,
    'Invested (Rs)': Number(g.invested.toFixed(2)),
    'Current/Sale Value (Rs)': Number(g.currentValue.toFixed(2)),
    'Profit/Loss (Rs)': Number(g.pl.toFixed(2)),
  }));
  if (fyRows.length > 0) {
    const fySheet = XLSX.utils.json_to_sheet(fyRows);
    fySheet['!cols'] = [{ wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 22 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, fySheet, 'FY-wise Summary');
  }

  // Generate filename with entity name and date
  const date = new Date().toISOString().split('T')[0];
  const filename = `${entityInfo.entityName}_Transactions_${date}.xlsx`;

  XLSX.writeFile(wb, filename);
};

export const exportHoldingsToExcel = (transactions, entityInfo) => {
  // Filter only held transactions
  const heldTransactions = transactions.filter(txn => txn.status === 'held');

  // Calculate summary
  const summary = heldTransactions.reduce((acc, txn) => {
    acc.totalInvested += txn.totalCost;
    acc.totalCurrentValue += txn.currentValue;
    acc.totalUnrealizedPL += txn.profit;
    return acc;
  }, { totalInvested: 0, totalCurrentValue: 0, totalUnrealizedPL: 0 });

  const overallReturn = summary.totalInvested > 0
    ? ((summary.totalUnrealizedPL / summary.totalInvested) * 100).toFixed(2)
    : 0;

  // Prepare holdings data
  const holdingsData = heldTransactions.map(txn => ({
    'Ticker': txn.ticker,
    'Share Name': txn.shareName,
    'Instrument Type': txn.instrumentType || 'Equity',
    'Exchange': txn.exchange,
    'Quantity': txn.quantity,
    'Avg Buy Price (₹)': txn.purchasePrice,
    'Current Price (₹)': txn.currentMarketPrice,
    'Invested Value (₹)': txn.totalCost,
    'Current Value (₹)': txn.currentValue,
    'Profit/Loss (₹)': txn.profit,
    'Return %': txn.returnPercentage,
    'CAGR %': txn.cagr,
    'Purchase Date': txn.purchaseDate
  }));

  // Create workbook
  const wb = XLSX.utils.book_new();

  // Summary sheet
  const summaryData = [
    ['HOLDINGS STATEMENT REPORT'],
    [''],
    ['Entity Name:', entityInfo.entityName],
    ['Portfolio ID:', entityInfo.portfolioId],
    ['PAN Number:', entityInfo.panNumber],
    ['Report Date:', new Date().toLocaleDateString('en-IN')],
    [''],
    ['SUMMARY'],
    ['Total Invested:', summary.totalInvested.toFixed(2)],
    ['Current Market Value:', summary.totalCurrentValue.toFixed(2)],
    ['Unrealized Profit/Loss:', summary.totalUnrealizedPL.toFixed(2)],
    ['Overall Return %:', overallReturn],
    ['Active Holdings:', heldTransactions.length],
    ['']
  ];

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

  // Holdings detail sheet
  const holdingsSheet = XLSX.utils.json_to_sheet(holdingsData);
  
  // Add column widths
  holdingsSheet['!cols'] = [
    { wch: 12 }, // Ticker
    { wch: 30 }, // Share Name
    { wch: 10 }, // Exchange
    { wch: 10 }, // Quantity
    { wch: 15 }, // Avg Buy Price
    { wch: 15 }, // Current Price
    { wch: 18 }, // Invested Value
    { wch: 18 }, // Current Value
    { wch: 15 }, // Profit/Loss
    { wch: 10 }, // Return %
    { wch: 10 }, // CAGR %
    { wch: 15 }  // Purchase Date
  ];

  XLSX.utils.book_append_sheet(wb, holdingsSheet, 'Holdings Details');

  // Generate filename
  const date = new Date().toISOString().split('T')[0];
  const filename = `${entityInfo.entityName}_Holdings_Report_${date}.xlsx`;
  
  XLSX.writeFile(wb, filename);
};

export const exportCompleteReport = (transactions, entityInfo) => {
  // Filter held and sold transactions
  const heldTransactions = transactions.filter(txn => txn.status === 'held');
  const soldTransactions = transactions.filter(txn => txn.status === 'sold');

  // Calculate summary
  const summary = transactions.reduce((acc, txn) => {
    acc.totalInvestment += txn.totalCost;
    acc.totalCurrentValue += txn.currentValue;
    acc.totalProfit += txn.profit;
    if (txn.status === 'sold') {
      acc.totalRealized += txn.profit;
    } else {
      acc.totalUnrealized += txn.profit;
    }
    return acc;
  }, { 
    totalInvestment: 0, 
    totalCurrentValue: 0, 
    totalProfit: 0, 
    totalRealized: 0, 
    totalUnrealized: 0 
  });

  const totalReturn = summary.totalInvestment > 0
    ? ((summary.totalProfit / summary.totalInvestment) * 100).toFixed(2)
    : 0;

  // Create workbook
  const wb = XLSX.utils.book_new();

  // Portfolio Summary Sheet
  const summaryData = [
    ['PORTFOLIO REPORT'],
    [''],
    ['Entity Information'],
    ['Name:', entityInfo.entityName],
    ['Type:', entityInfo.entityType],
    ['Portfolio ID:', entityInfo.portfolioId],
    ['PAN Number:', entityInfo.panNumber],
    ['Report Date:', new Date().toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })],
    [''],
    ['Portfolio Summary'],
    ['Total Investment (₹):', summary.totalInvestment.toFixed(2)],
    ['Current Value (₹):', summary.totalCurrentValue.toFixed(2)],
    ['Total Profit/Loss (₹):', summary.totalProfit.toFixed(2)],
    ['Overall Return %:', totalReturn],
    ['Realized P/L (₹):', summary.totalRealized.toFixed(2)],
    ['Unrealized P/L (₹):', summary.totalUnrealized.toFixed(2)],
    [''],
    ['Portfolio Breakdown'],
    ['Total Transactions:', transactions.length],
    ['Active Holdings:', heldTransactions.length],
    ['Sold Positions:', soldTransactions.length]
  ];

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Portfolio Summary');

  // Holdings Sheet (only held)
  if (heldTransactions.length > 0) {
    const holdingsData = heldTransactions.map(txn => ({
      'Ticker': txn.ticker,
      'Share Name': txn.shareName,
      'Instrument Type': txn.instrumentType || 'Equity',
      'Exchange': txn.exchange,
      'Quantity': txn.quantity,
      'Avg Buy Price (₹)': txn.purchasePrice,
      'Current Price (₹)': txn.currentMarketPrice,
      'Invested Value (₹)': txn.totalCost,
      'Current Value (₹)': txn.currentValue,
      'Profit/Loss (₹)': txn.profit,
      'Return %': txn.returnPercentage,
      'CAGR %': txn.cagr,
      'Purchase Date': txn.purchaseDate
    }));

    const holdingsSheet = XLSX.utils.json_to_sheet(holdingsData);
    holdingsSheet['!cols'] = Array(12).fill({ wch: 15 });
    XLSX.utils.book_append_sheet(wb, holdingsSheet, 'Current Holdings');
  }

  // Sold Transactions Sheet
  if (soldTransactions.length > 0) {
    const soldData = soldTransactions.map(txn => ({
      'Ticker': txn.ticker,
      'Share Name': txn.shareName,
      'Instrument Type': txn.instrumentType || 'Equity',
      'Exchange': txn.exchange,
      'Quantity': txn.quantity,
      'Buy Price (₹)': txn.purchasePrice,
      'Sell Price (₹)': txn.sellPrice,
      'Purchase Date': txn.purchaseDate,
      'Sell Date': txn.sellDate,
      'Total Cost (₹)': txn.totalCost,
      'Sale Value (₹)': txn.currentValue,
      'Profit/Loss (₹)': txn.profit,
      'Return %': txn.returnPercentage,
      'CAGR %': txn.cagr
    }));

    const soldSheet = XLSX.utils.json_to_sheet(soldData);
    soldSheet['!cols'] = Array(13).fill({ wch: 15 });
    XLSX.utils.book_append_sheet(wb, soldSheet, 'Sold Positions');
  }

  // All Transactions Sheet
  const allTransactionsData = transactions.map(txn => ({
    'Ticker': txn.ticker,
    'Share Name': txn.shareName,
    'Instrument Type': txn.instrumentType || 'Equity',
    'Exchange': txn.exchange,
    'Quantity': txn.quantity,
    'Purchase Price (₹)': txn.purchasePrice,
    'Purchase Date': txn.purchaseDate,
    'Current/Sell Price (₹)': txn.status === 'sold' ? txn.sellPrice : txn.currentMarketPrice,
    'Sell Date': txn.sellDate || '-',
    'Total Cost (₹)': txn.totalCost,
    'Current/Sale Value (₹)': txn.currentValue,
    'Profit/Loss (₹)': txn.profit,
    'Return %': txn.returnPercentage,
    'CAGR %': txn.cagr,
    'Status': txn.status === 'sold' ? 'Sold' : 'Held'
  }));

  const allTransactionsSheet = XLSX.utils.json_to_sheet(allTransactionsData);
  allTransactionsSheet['!cols'] = Array(14).fill({ wch: 15 });
  XLSX.utils.book_append_sheet(wb, allTransactionsSheet, 'All Transactions');

  // Generate filename
  const date = new Date().toISOString().split('T')[0];
  const filename = `${entityInfo.entityName}_Complete_Report_${date}.xlsx`;
  
  XLSX.writeFile(wb, filename);
};

export const exportCapitalGainsToExcel = (transactions, entityInfo, portfolios = []) => {
  const pmap = Object.fromEntries((portfolios || []).map((p) => [p.id, p.name]));
  // Filter only sold transactions
  const soldTransactions = transactions.filter(t => t.status === 'sold');

  // Calculate capital gains with classification
  const capitalGainsData = soldTransactions.map(transaction => {
    const purchaseDate = new Date(transaction.purchaseDate);
    const sellDate = new Date(transaction.sellDate);

    const holdingPeriodDays = Math.floor((sellDate - purchaseDate) / (1000 * 60 * 60 * 24));
    const holdingPeriodYears = (holdingPeriodDays / 365).toFixed(2);
    const equity = isEquityTax(transaction.instrumentType);
    const longTerm = equity ? holdingPeriodDays > 365 : holdingPeriodDays > 1095;
    const gainType = longTerm ? 'LTCG' : 'STCG';

    return {
      ...transaction,
      portfolioName: pmap[transaction.portfolioId] || '—',
      holdingPeriodDays,
      holdingPeriodYears,
      gainType,
      equity,
      fy: getFY(transaction.sellDate),
    };
  });

  // Separate LTCG and STCG
  const ltcgTransactions = capitalGainsData.filter(t => t.gainType === 'LTCG');
  const stcgTransactions = capitalGainsData.filter(t => t.gainType === 'STCG');

  // Calculate summaries
  const ltcgSummary = ltcgTransactions.reduce((acc, t) => acc + t.profit, 0);
  const stcgSummary = stcgTransactions.reduce((acc, t) => acc + t.profit, 0);
  const totalCapitalGains = ltcgSummary + stcgSummary;

  // Create workbook
  const wb = XLSX.utils.book_new();

  // Summary Sheet
  const summaryData = [
    ['CAPITAL GAINS REPORT'],
    [''],
    ['Entity Information'],
    ['Name:', entityInfo.entityName],
    ['Portfolio ID:', entityInfo.portfolioId],
    ['PAN Number:', entityInfo.panNumber],
    ['Report Date:', new Date().toLocaleDateString('en-IN')],
    [''],
    ['Capital Gains Summary'],
    ['Total Capital Gains (₹):', totalCapitalGains.toFixed(2)],
    ['Long-term Capital Gains - LTCG (₹):', ltcgSummary.toFixed(2)],
    ['Short-term Capital Gains - STCG (₹):', stcgSummary.toFixed(2)],
    [''],
    ['Tax Information (India)'],
    ['LTCG Tax Rate:', '10% on gains exceeding ₹1 lakh'],
    ['STCG Tax Rate:', '15% on all gains'],
    [''],
    ['Note:', 'LTCG = Holdings > 12 months, STCG = Holdings ≤ 12 months']
  ];

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

  // Financial Year-wise Summary Sheet
  const fyGroups = {};
  capitalGainsData.forEach((t) => {
    const g = fyGroups[t.fy] || { fy: t.fy, eqLT: 0, eqST: 0, debt: 0, total: 0, count: 0 };
    if (t.equity && t.gainType === 'LTCG') g.eqLT += t.profit;
    else if (t.equity) g.eqST += t.profit;
    else g.debt += t.profit;
    g.total += t.profit;
    g.count += 1;
    fyGroups[t.fy] = g;
  });
  const fyRows = Object.values(fyGroups).sort((a, b) => b.fy.localeCompare(a.fy)).map((g) => ({
    'Financial Year': g.fy,
    'Equity LTCG (Rs)': Number(g.eqLT.toFixed(2)),
    'Equity STCG (Rs)': Number(g.eqST.toFixed(2)),
    'Debt / Other (Rs)': Number(g.debt.toFixed(2)),
    'Total Gain (Rs)': Number(g.total.toFixed(2)),
    'Transactions': g.count,
  }));
  if (fyRows.length > 0) {
    const fySheet = XLSX.utils.json_to_sheet(fyRows);
    fySheet['!cols'] = [{ wch: 16 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, fySheet, 'FY-wise Summary');
  }
  if (ltcgTransactions.length > 0) {
    const ltcgData = ltcgTransactions.map(txn => ({
      'Portfolio': txn.portfolioName,
      'Ticker': txn.ticker,
      'Share Name': txn.shareName,
      'Instrument Type': txn.instrumentType || 'Equity',
      'Exchange': txn.exchange,
      'Quantity': txn.quantity,
      'Purchase Date': txn.purchaseDate,
      'Sell Date': txn.sellDate,
      'Financial Year': txn.fy,
      'Holding Period (Years)': txn.holdingPeriodYears,
      'Holding Period (Days)': txn.holdingPeriodDays,
      'Buy Price (₹)': txn.purchasePrice,
      'Sell Price (₹)': txn.sellPrice,
      'Total Cost (₹)': txn.totalCost,
      'Sale Value (₹)': txn.currentValue,
      'Capital Gain (₹)': txn.profit,
      'Return %': txn.returnPercentage,
      'Type': 'LTCG'
    }));

    const ltcgSheet = XLSX.utils.json_to_sheet(ltcgData);
    ltcgSheet['!cols'] = Array(15).fill({ wch: 15 });
    XLSX.utils.book_append_sheet(wb, ltcgSheet, 'Long-term Gains (LTCG)');
  }

  // STCG Sheet
  if (stcgTransactions.length > 0) {
    const stcgData = stcgTransactions.map(txn => ({
      'Portfolio': txn.portfolioName,
      'Ticker': txn.ticker,
      'Share Name': txn.shareName,
      'Instrument Type': txn.instrumentType || 'Equity',
      'Exchange': txn.exchange,
      'Quantity': txn.quantity,
      'Purchase Date': txn.purchaseDate,
      'Sell Date': txn.sellDate,
      'Financial Year': txn.fy,
      'Holding Period (Years)': txn.holdingPeriodYears,
      'Holding Period (Days)': txn.holdingPeriodDays,
      'Buy Price (₹)': txn.purchasePrice,
      'Sell Price (₹)': txn.sellPrice,
      'Total Cost (₹)': txn.totalCost,
      'Sale Value (₹)': txn.currentValue,
      'Capital Gain (₹)': txn.profit,
      'Return %': txn.returnPercentage,
      'Type': 'STCG'
    }));

    const stcgSheet = XLSX.utils.json_to_sheet(stcgData);
    stcgSheet['!cols'] = Array(15).fill({ wch: 15 });
    XLSX.utils.book_append_sheet(wb, stcgSheet, 'Short-term Gains (STCG)');
  }

  // Generate filename
  const date = new Date().toISOString().split('T')[0];
  const filename = `${entityInfo.entityName}_Capital_Gains_${date}.xlsx`;
  
  XLSX.writeFile(wb, filename);
};
